import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as smsService from '../../services/smsService';

const { TextArea } = Input;

const SmsChannel: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<smsService.SmsChannel[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchName, setSearchName] = useState('');
  const [searchProvider, setSearchProvider] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingChannel, setEditingChannel] = useState<smsService.SmsChannel | null>(null);
  const [form] = Form.useForm();

  const loadChannels = async () => {
    setLoading(true);
    try {
      const res = await smsService.listChannels({
        page, pageSize,
        name: searchName || undefined,
        provider: searchProvider || undefined,
      });
      setChannels(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载渠道列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadChannels(); }, [page, pageSize]);

  const handleSearch = () => { setPage(1); loadChannels(); };
  const handleReset = () => { setSearchName(''); setSearchProvider(''); setPage(1); loadChannels(); };

  const handleAdd = () => {
    setEditingChannel(null);
    form.resetFields();
    form.setFieldsValue({ status: 1, isDefault: 1, provider: 'tencent' });
    setModalVisible(true);
  };

  const handleEdit = (record: smsService.SmsChannel) => {
    setEditingChannel(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await smsService.deleteChannel(id);
      message.success('删除成功');
      loadChannels();
    } catch (err: any) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        name: values.name,
        code: editingChannel?.code || `tencent_${Date.now()}`,
        endpoint: '',
        region: '',
        isDefault: editingChannel?.isDefault ?? 1,
        status: editingChannel?.status ?? 1,
      };
      if (editingChannel) {
        await smsService.updateChannel(editingChannel.id, payload);
        message.success('更新成功');
      } else {
        await smsService.createChannel(payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadChannels();
    } catch { }
  };

  const columns: ColumnsType<smsService.SmsChannel> = [
    { title: '渠道名称', dataIndex: 'name', key: 'name', width: 180, ellipsis: true },
    {
      title: '服务商', dataIndex: 'provider', key: 'provider', width: 120,
      render: (v) => smsService.providerOptions.find(o => o.value === v)?.label || v,
    },
    { title: 'secret_id', dataIndex: 'accessKey', key: 'accessKey', width: 220, ellipsis: true },
    { title: 'sdk_app_id', dataIndex: 'appId', key: 'appId', width: 160, ellipsis: true },
    { title: 'sign_name', dataIndex: 'signName', key: 'signName', width: 180, ellipsis: true },
    { title: '备注', dataIndex: 'remark', key: 'remark', width: 260, ellipsis: true },
    {
      title: '操作', key: 'action', width: 140, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除此渠道？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="短信渠道"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadChannels}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增渠道</Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }}>
          <Input placeholder="渠道名称" value={searchName} onChange={(e) => setSearchName(e.target.value)} style={{ width: 150 }} allowClear />
          <Select placeholder="服务商" value={searchProvider || undefined} onChange={setSearchProvider} style={{ width: 140 }} allowClear>
            {smsService.providerOptions.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
          </Select>
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
        <Table columns={columns} dataSource={channels} rowKey="id" loading={loading}
          scroll={{ x: 1260 }}
          pagination={{ current: page, pageSize, total, showSizeChanger: true, showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); } }} />
      </Card>

      <Modal title={editingChannel ? '编辑渠道' : '新增渠道'} open={modalVisible} onCancel={() => setModalVisible(false)} onOk={handleSubmit} width={600}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="渠道名称" rules={[{ required: true, message: '请输入渠道名称' }]}>
            <Input placeholder="请输入渠道名称" />
          </Form.Item>
          <Form.Item name="provider" label="服务商" rules={[{ required: true, message: '请选择服务商' }]}>
            <Select>
              {smsService.providerOptions.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="accessKey" label="secret_id">
            <Input placeholder="请输入 secret_id" />
          </Form.Item>
          <Form.Item name="secretKey" label="secret_key">
            <Input.Password placeholder="请输入 secret_key" />
          </Form.Item>
          <Form.Item name="appId" label="sdk_app_id">
            <Input placeholder="请输入 sdk_app_id" />
          </Form.Item>
          <Form.Item name="signName" label="sign_name">
            <Input placeholder="请输入 sign_name" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SmsChannel;
